/**
 * Typed synchronous event bus (architecture §3.4). The bus is for **notifications** (UI toasts, achievements, the
 * telemetry ring): state changes that must happen go through services (`ctx.services`), so they stay synchronous,
 * ordered and testable. The app instantiates one `EventBus<GameEvents>` and exposes the last 200 events as
 * `__marg.events()`.
 */

/** One recorded emission (for `__marg.events()`); `simMs`/`presentMs` come from the optional `stamp` source. */
export interface BusRecord { type: string; payload: unknown; simMs: number; presentMs: number }

export interface EventBusOptions {
  /** Handler errors are caught and reported here (default: `console.error`); emission continues with the next handler. */
  onError?: (err: unknown, type: string) => void;
  /** Supplies the clock instants stored with each record (the app passes the `Clock`). */
  stamp?: () => { simMs: number; presentMs: number };
  /** Ring size of the history (default 200). */
  historySize?: number;
}

type Handler = (payload: never) => void;

/**
 * `E` is an interface of event name → payload (`object`, not `Record<string, unknown>`: interfaces have no index
 * signature). Handlers run synchronously in registration order; unsubscribing during an emit is safe.
 */
export class EventBus<E extends object> {
  private readonly handlers = new Map<string, Handler[]>();
  private readonly history: BusRecord[] = [];
  private readonly opts: Required<Pick<EventBusOptions, 'historySize'>> & EventBusOptions;

  constructor(opts: EventBusOptions = {}) {
    this.opts = { historySize: 200, ...opts };
  }

  /** Subscribes; returns the unsubscribe function. */
  on<K extends keyof E & string>(type: K, fn: (payload: E[K]) => void): () => void {
    const list = this.handlers.get(type) ?? [];
    list.push(fn as Handler);
    this.handlers.set(type, list);
    let active = true;
    return () => {
      if (!active) return;
      active = false;
      const cur = this.handlers.get(type);
      if (!cur) return;
      const i = cur.indexOf(fn as Handler);
      if (i >= 0) cur.splice(i, 1);
    };
  }

  /** Subscribes for the next emission only. */
  once<K extends keyof E & string>(type: K, fn: (payload: E[K]) => void): () => void {
    const off = this.on(type, (p) => { off(); fn(p); });
    return off;
  }

  /** Synchronous, registration order; handler errors are caught + reported. */
  emit<K extends keyof E & string>(type: K, payload: E[K]): void {
    const st = this.opts.stamp?.() ?? { simMs: 0, presentMs: 0 };
    this.history.push({ type, payload, simMs: st.simMs, presentMs: st.presentMs });
    if (this.history.length > this.opts.historySize) this.history.splice(0, this.history.length - this.opts.historySize);
    const list = this.handlers.get(type);
    if (!list || list.length === 0) return;
    for (const fn of list.slice()) {
      try {
        (fn as (p: E[K]) => void)(payload);
      } catch (err) {
        if (this.opts.onError) this.opts.onError(err, type);
        else console.error(`[bus] handler for "${type}" threw`, err);
      }
    }
  }

  /** The last `n` (default: all kept, ≤ historySize) emissions, oldest first. */
  events(n?: number): BusRecord[] {
    return n === undefined ? this.history.slice() : this.history.slice(Math.max(0, this.history.length - n));
  }

  /** Number of live handlers for a type (tests, leak checks). */
  listenerCount<K extends keyof E & string>(type: K): number {
    return this.handlers.get(type)?.length ?? 0;
  }
}
